import React, { useState, useEffect } from 'react';
import { Board as BoardType, Column as ColumnType, Card as CardType, BoardMember, columnApi, cardApi, labelApi, getApiErrorMessage, wasBoardAccessDenied } from '../services/api';
import { Column } from './Column';
import { Navbar } from './Navbar';
import { useAuth } from '../contexts/AuthContext';
import { BoardMembers } from './BoardMembers';
import { FilterPanel, FilterState } from './FilterPanel';
import { AdBanner } from './AdBanner';
import { Group, Plus, Filter } from 'iconoir-react';
import { LimitModal } from './LimitModal';

// Fonction pour convertir une couleur simple en dégradé
const getGradientFromColor = (color: string): string => {
  const gradientMap: Record<string, string> = {
    '#5BC279': 'linear-gradient(135deg, #5BC279 0%, #4FA368 100%)',
    '#E5CA5C': 'linear-gradient(135deg, #E5CA5C 0%, #C2AA4A 100%)',
    '#E5A333': 'linear-gradient(135deg, #E5A333 0%, #C28A2B 100%)',
    '#E58D85': 'linear-gradient(135deg, #E58D85 0%, #C27870 100%)',
    '#B38EE5': 'linear-gradient(135deg, #B38EE5 0%, #9678C2 100%)',
    '#66A8CC': 'linear-gradient(135deg, #66A8CC 0%, #558DAD 100%)',
    '#85CCDB': 'linear-gradient(135deg, #85CCDB 0%, #70ADB9 100%)',
    '#A8C585': 'linear-gradient(135deg, #A8C585 0%, #8DA670 100%)',
    '#E591A8': 'linear-gradient(135deg, #E591A8 0%, #C27A8D 100%)',
    '#8FA1A8': 'linear-gradient(135deg, #8FA1A8 0%, #78898F 100%)',
  };
  return gradientMap[color] || color;
};

interface BoardProps {
  board: BoardType;
  boardMembers: BoardMember[];
  acceptedInvitationIds: number[];
  onMembersRefresh: () => void;
  onMemberRemovedFromCards: (userId: number) => void;
  onUpdate: (board: BoardType) => void;
  onRefresh: () => void;
}

export const Board: React.FC<BoardProps> = ({ board, boardMembers, acceptedInvitationIds, onMembersRefresh, onMemberRemovedFromCards, onUpdate, onRefresh }) => {
  const { user } = useAuth();
  const [draggedCard, setDraggedCard] = useState<CardType | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<number | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<ColumnType | null>(null);
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [cardIdToOpen, setCardIdToOpen] = useState<number | null>(null);
  const [showMembers, setShowMembers] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [boardLabels, setBoardLabels] = useState<Array<{ name: string; color: string }>>([]);
  const [showColumnLimitModal, setShowColumnLimitModal] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    keyword: '',
    authorIds: [],
    memberIds: [],
    labelNames: [],
    createdAfter: '',
    createdBefore: '',
    updatedAfter: '',
    updatedBefore: '',
  });

  // Récupérer le rôle de l'utilisateur pour ce board
  // Si l'utilisateur est le propriétaire du tableau, il a le rôle admin
  const isOwner = user && board.user_id === user.id;
  // Sinon, chercher le rôle dans les membres du tableau
  const boardMember = boardMembers.find(m => m.email === user?.email);
  // Le rôle owner est traité comme admin côté UI.
  const ROLE_MAP: Record<'owner' | 'admin' | 'editor' | 'visitor', 'admin' | 'editor' | 'visitor'> = {
    owner: 'admin', admin: 'admin', editor: 'editor', visitor: 'visitor',
  };
  const userRole = isOwner ? 'admin' : ROLE_MAP[boardMember?.role ?? 'visitor'];
  const canEditBoard = userRole === 'admin' || userRole === 'editor';

  // Appliquer la couleur de fond du tableau au body
  useEffect(() => {
    const originalBackground = document.body.style.background;
    
    if (board.background_color) {
      // Appliquer le dégradé de la couleur personnalisée
      document.body.style.background = getGradientFromColor(board.background_color);
    } else {
      // Réinitialiser au dégradé par défaut (déjà défini dans le CSS)
      document.body.style.background = '';
    }

    // Nettoyer lors du démontage du composant
    return () => {
      document.body.style.background = originalBackground;
    };
  }, [board.background_color]);

  // Détecter le paramètre card dans l'URL au chargement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cardId = urlParams.get('card');
    if (cardId) {
      setCardIdToOpen(parseInt(cardId, 10));
    }
  }, []);

  const handleColumnUpdate = async (updatedColumn: ColumnType) => {
    try {
      await columnApi.update(board.id, updatedColumn.id, { 
        title: updatedColumn.title,
        position: updatedColumn.position 
      });
      onRefresh();
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour de la colonne', err);
      if (err.response?.status === 403) {
        await onRefresh();
        if (!wasBoardAccessDenied()) {
          window.alert(getApiErrorMessage(err, 'Vous n\'avez pas la permission de modifier des colonnes.'));
        }
      }
    }
  };

  const handleColumnDelete = async (columnId: number) => {
    try {
      await columnApi.delete(board.id, columnId);
      onRefresh();
    } catch (err: any) {
      console.error('Erreur lors de la suppression de la colonne', err);
      if (err.response?.status === 403) {
        await onRefresh();
        if (!wasBoardAccessDenied()) {
          window.alert(getApiErrorMessage(err, 'Seuls les administrateurs peuvent supprimer des colonnes.'));
        }
      }
    }
  };

  const handleCardMove = async (card: CardType, fromColumnId: number, toColumnId: number) => {
    try {
      const targetColumn = board.columns.find(col => col.id === toColumnId);
      const newPosition = targetColumn?.cards.length || 0;
      
      await cardApi.move(card.id, {
        column_id: toColumnId,
        position: newPosition
      });
      
      onRefresh();
    } catch (err: any) {
      console.error('Erreur lors du déplacement de la carte', err);
      if (err.response?.status === 403) {
        await onRefresh();
        if (!wasBoardAccessDenied()) {
          window.alert(getApiErrorMessage(err, 'Vous n\'avez pas la permission de déplacer des cartes.'));
        }
      }
    }
  };

  const handleDragStart = (card: CardType) => {
    setDraggedCard(card);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDragOverColumnId(null);
    }
  };

  const handleDragEnter = (columnId: number) => {
    setDragOverColumnId(columnId);
  };

  const handleDrop = async (e: React.DragEvent, columnId: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedCard) {
      if (draggedCard.column_id !== columnId) {
        await handleCardMove(draggedCard, draggedCard.column_id, columnId);
      }
      setDraggedCard(null);
      setDragOverColumnId(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDragOverColumnId(null);
  };

  const loadBoardLabels = async () => {
    try {
      const response = await labelApi.getBoardLabels(board.id);
      setBoardLabels(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des étiquettes', error);
    }
  };

  useEffect(() => {
    loadBoardLabels();
  }, [board.id]);

  const handleColumnDragStart = (column: ColumnType) => {
    setDragOverColumn(column);
  };

  const handleColumnDragEnd = () => {
    setDragOverColumn(null);
  };

  const handleColumnDrop = async (e: React.DragEvent, targetColumn: ColumnType) => {
    e.preventDefault();
    e.stopPropagation();
    
    const draggedColumnData = e.dataTransfer.getData('column');
    if (!draggedColumnData) return;
    
    const draggedCol: ColumnType = JSON.parse(draggedColumnData);
    
    if (draggedCol.id === targetColumn.id) return;
    
    try {
      await columnApi.move(draggedCol.id, {
        position: targetColumn.position
      });
      onRefresh();
    } catch (err: any) {
      console.error('Erreur lors du déplacement de la colonne', err);
      if (err.response?.status === 403) {
        await onRefresh();
        if (!wasBoardAccessDenied()) {
          window.alert(getApiErrorMessage(err, 'Seuls les administrateurs peuvent déplacer des colonnes.'));
        }
      }
    }

    setDragOverColumn(null);
  };

  const handleAddColumn = async () => {
    if (newColumnTitle.trim()) {
      try {
        await columnApi.create(board.id, { 
          title: newColumnTitle.trim(),
          position: board.columns.length
        });
        setNewColumnTitle('');
        setIsAddingColumn(false);
        onRefresh();
      } catch (err: any) {
        if (
          err.response?.status === 422 &&
          err.response?.data?.error === 'column_limit_reached'
        ) {
          setShowColumnLimitModal(true);
        } else if (err.response?.status === 403) {
          await onRefresh();
          if (!wasBoardAccessDenied()) {
            window.alert(getApiErrorMessage(err, 'Vous n\'avez pas la permission de créer des colonnes.'));
          }
        } else {
          console.error('Erreur lors de la création de la colonne', err);
        }
      }
    }
  };

  const filterCards = (cards: CardType[]): CardType[] => {
    return cards.filter(card => {
      // Filtre par mot-clé
      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase();
        const matchesTitle = card.title.toLowerCase().includes(keyword);
        const matchesDescription = card.description?.toLowerCase().includes(keyword);
        const matchesLabels = card.labels?.some(label => label.name.toLowerCase().includes(keyword));
        if (!matchesTitle && !matchesDescription && !matchesLabels) {
          return false;
        }
      }

      // Filtre par membres affectés
      if (filters.memberIds.length > 0) {
        const cardMemberIds = card.users?.map((u: any) => u.id) || [];
        const hasMatchingMember = filters.memberIds.some(id => cardMemberIds.includes(id));
        if (!hasMatchingMember) {
          return false;
        }
      }

      // Filtre par étiquettes
      if (filters.labelNames.length > 0) {
        const cardLabelNames = card.labels?.map(l => l.name) || [];
        const hasMatchingLabel = filters.labelNames.some(name => cardLabelNames.includes(name));
        if (!hasMatchingLabel) {
          return false;
        }
      }

      // Filtre par date de création
      if (filters.createdAfter && new Date(card.created_at) < new Date(filters.createdAfter)) {
        return false;
      }
      if (filters.createdBefore && new Date(card.created_at) > new Date(filters.createdBefore)) {
        return false;
      }

      // Filtre par date de dernière activité
      if (filters.updatedAfter && new Date(card.updated_at) < new Date(filters.updatedAfter)) {
        return false;
      }
      if (filters.updatedBefore && new Date(card.updated_at) > new Date(filters.updatedBefore)) {
        return false;
      }

      return true;
    });
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  return (
    <>
      <Navbar contextRole={userRole} boardName={board.title} boardId={board.id} />
      <div className="board-container">
        <div className="board-header">
          <h1 className="board-title">{board.title}</h1>
          <div className="board-actions">
            <button 
              className="btn btn-filter"
              onClick={() => setShowFilters(true)}
              title="Filtrer les cartes"
            >
              <Filter width={18} height={18} strokeWidth={2} />
            </button>
            <button 
              className="btn btn-members"
              onClick={() => setShowMembers(true)}
              title="Gérer les membres"
            >
              <Group width={18} height={18} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Bannière publicitaire */}
        <AdBanner position="top" />

        <div className="columns-container">
          {board.columns.map((column) => (
          <div key={column.id} className="kanban-column-wrapper">
            <Column
              column={{
                ...column,
                cards: filterCards(column.cards)
              }}
              boardId={board.id}
              onUpdate={handleColumnUpdate}
              onDelete={handleColumnDelete}
              onCardMove={handleCardMove}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              isDragOver={dragOverColumnId === column.id}
              onRefresh={onRefresh}
              onColumnDragStart={handleColumnDragStart}
              onColumnDragEnd={handleColumnDragEnd}
              onColumnDrop={handleColumnDrop}
              isColumnDragOver={dragOverColumn?.id === column.id}
              userRole={userRole}
              cardIdToOpen={cardIdToOpen}
            />
          </div>
          ))}

          {canEditBoard && (
            isAddingColumn ? (
              <div className="kanban-column">
                <div className="card-form">
                  <textarea
                    value={newColumnTitle}
                    onChange={(e) => setNewColumnTitle(e.target.value)}
                    placeholder="Titre de la colonne..."
                    autoFocus
                  />
                  <div className="card-form-buttons">
                    <button className="btn btn-primary" onClick={handleAddColumn}>
                      Ajouter
                    </button>
                    <button
                      className="btn btn-cancel"
                      onClick={() => {
                        setIsAddingColumn(false);
                        setNewColumnTitle('');
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                className="add-column-btn"
                onClick={() => setIsAddingColumn(true)}
              >
                <Plus width={16} height={16} strokeWidth={2} />
                <span>Ajouter une colonne</span>
              </button>
            )
          )}
        </div>
      </div>

      <BoardMembers
        isOpen={showMembers}
        onClose={() => setShowMembers(false)}
        boardId={board.id}
        members={boardMembers}
        acceptedInvitationIds={acceptedInvitationIds}
        onMembersRefresh={onMembersRefresh}
        onMemberRemovedFromCards={onMemberRemovedFromCards}
        userRole={userRole}
        boardOwnerId={board.user_id}
      />

      <FilterPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        members={boardMembers}
        labels={boardLabels}
        onFilterChange={handleFilterChange}
      />

      <LimitModal
        isOpen={showColumnLimitModal}
        onClose={() => setShowColumnLimitModal(false)}
        message={
          <>
            Ce tableau contient déjà le maximum autorisé de <strong>20 colonnes</strong>.
          </>
        }
      />
    </>
  );
};
